import { AppwriteException, OAuthProvider, type Models } from 'appwrite';

import { account } from '@/lib/appwrite';
import { ValidationError } from './errors';

/**
 * The single entry point to Appwrite Authentication — the web port of
 * `auth_repository.dart`.
 *
 * The Appwrite SDK persists the session itself, so there is no separate local
 * "logged in" flag to keep in sync: `getCurrentUser()` is the source of truth.
 */

export type AppwriteUser = Models.User<Models.DefaultPreferences>;

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function looksLikeEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

/** Returns the signed-in user, or `null` when there is no valid session. */
export async function getCurrentUser(): Promise<AppwriteUser | null> {
  try {
    return await account.get();
  } catch (error) {
    if (error instanceof AppwriteException && (error.code === 401 || error.code === 403)) {
      return null;
    }
    throw error;
  }
}

export async function signInWithEmail(email: string, password: string): Promise<AppwriteUser> {
  const trimmed = email.trim();
  if (trimmed === '') throw new ValidationError('Email is required.');
  if (password === '') throw new ValidationError('Password is required.');

  // A previous sign-out that never reached the server can leave a live session
  // in this browser, which makes createEmailPasswordSession fail with
  // "a session is active". Clearing this browser's session first makes sign-in
  // idempotent (other devices are never touched).
  await clearCurrentSession();

  await account.createEmailPasswordSession({ email: trimmed, password });
  return await account.get();
}

/**
 * Starts the OAuth2 redirect flow. This call **navigates away** from the app,
 * so nothing after it runs — the user comes back at `successUrl`.
 *
 * The current Appwrite session is cleared first so this flow does not revoke
 * sessions on the user's other devices.
 */
export async function startOAuthSignIn(
  provider: OAuthProvider,
  successUrl: string,
  failureUrl: string,
): Promise<void> {
  await clearCurrentSession();
  account.createOAuth2Session({ provider, success: successUrl, failure: failureUrl });
}

export { OAuthProvider };

export async function signOut(): Promise<void> {
  await clearCurrentSession();
}

async function clearCurrentSession(): Promise<void> {
  try {
    await account.deleteSession({ sessionId: 'current' });
  } catch (error) {
    // 401/404 → nothing to clear in this browser.
    if (error instanceof AppwriteException && error.code !== 401 && error.code !== 404) {
      console.warn('clearCurrentSession:', error.message);
    }
  }
}

